import { REHYDRATE } from 'redux-persist';
import { LOGGED_OUT } from 'actions/types';
import * as invest from '../types';
import * as localTypes from '../actions/types';
import { RECENTLY_VIEWED_MAX_COUNT } from '../constants';
import { getTradingAccountStatusObj } from '../utils';
function getMTimestamp(dateStr) {
    return (dateStr ? new Date(dateStr) : new Date()).getTime() / 60000;
}
function updateStockMetaCache(cache, assets) {
    if (assets.length) {
        const newCache = { ...cache };
        assets.forEach((asset) => {
            const iconObj = 'icon' in asset ? { iconUrl: asset.icon } : {};
            const nameObj = 'name' in asset ? { name: asset.name } : {};
            const originalNameObj = 'originalName' in asset ? { originalName: asset.originalName } : {};
            const id = 'id' in asset ? asset.id : asset.assetId;
            const description = 'description' in asset ? asset.description : undefined;
            const shortDescription = 'shortDescription' in asset ? asset.shortDescription : undefined;
            const averageAnnualReturn = 'averageAnnualReturn' in asset ? asset.averageAnnualReturn : undefined;
            newCache[id] = {
                ...cache[id],
                id,
                symbol: asset.symbol,
                ...iconObj,
                ...nameObj,
                ...originalNameObj,
                ...(description ? { description } : {}),
                ...(shortDescription ? { shortDescription } : {}),
                ...(averageAnnualReturn && { averageAnnualReturn }),
            };
        });
        return newCache;
    }
    return cache;
}
function updateCurrentPriceCache(cache, assets) {
    if (assets.length) {
        const newCache = { ...cache };
        const mTimestamp = getMTimestamp();
        assets.forEach((asset) => {
            const { sharePrice, sharePriceCurrency } = asset;
            const id = 'id' in asset ? asset.id : asset.assetId;
            if (sharePrice !== undefined && sharePriceCurrency !== undefined) {
                newCache[id] = {
                    value: sharePrice,
                    currency: sharePriceCurrency,
                    mTimestamp,
                };
            }
        });
        return newCache;
    }
    return cache;
}
function updatePriceChangeCache(cache, assets) {
    if (assets.length) {
        const newCache = { ...cache };
        assets.forEach((asset) => {
            const { sharePriceChange, sharePriceChangePct } = asset;
            const id = 'id' in asset ? asset.id : asset.assetId;
            if (typeof sharePriceChange === 'number' && typeof sharePriceChangePct === 'number') {
                newCache[id] = {
                    value: sharePriceChange,
                    percent: sharePriceChangePct,
                };
            }
        });
        return newCache;
    }
    return cache;
}
/**
 * It won’t insert it if it’s already there.
 */
function insertOrderId(id, idArray, orderCache) {
    if (idArray.findIndex((orderId) => orderId === id) !== -1) {
        return idArray;
    }
    const result = [id, ...idArray];
    const timestamps = {};
    for (let index = 0; index < result.length; index += 1) {
        const order = orderCache[result[index]];
        if (order) {
            timestamps[order.id] = new Date(order.timestamp).getTime();
        }
        else {
            return [id, ...idArray]; // some orders were not cached, we cannot perform sorting. Let’s put this order in front
        }
    }
    result.sort((a, b) => timestamps[b] - timestamps[a]);
    return result;
}
export const initialState = {
    connectedAccount: null,
    caches: {},
    priceChangeCaches: {},
    currentPriceCaches: {},
    ordersCache: {},
    orderIdForAssetIdCache: {},
    pendingOrderIdForAssetIdCache: {},
    tradingAccountPositionsCache: {},
    watchlists: [],
    topTrendingForTradingAccount: {},
    mostWatchedStocks: [],
    mostHeldStocks: [],
    losers: [],
    gainers: [],
    kycDocumentType: undefined,
    tierFxRateMap: undefined,
    categoryIdSectorMap: undefined,
    hasCompletedOnboardingFunnel: undefined,
    requiredNationalityIdentifiers: undefined,
    spaceTradingAccounts: {},
    queuedOrdersForTradingAccountCache: {},
    pendingOrdersForTradingAccountCache: {},
    firstAccountJustBecameActive: false,
};
const handledSectorNames = [
    'Finance',
    'Health',
    'Tech',
    'Industrials',
    'Energy',
    'Consumer Goods',
    'Property',
    'Services',
    'Mining',
    'Entertainment',
    'Food & Drink',
    'Cars',
    'Logistics',
    'Construction',
    'Telco',
    'Utilities',
    'Travel',
    'Fashion',
];
const toTitleCase = (str) => str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
const omitPausedOnboarding = (pausedOnboarding, accountType) => {
    if (!pausedOnboarding?.[accountType]) {
        return pausedOnboarding;
    }
    const { [accountType]: _removed, ...rest } = pausedOnboarding;
    return rest;
};
const OPENED_ACCOUNT_STATUSES = [
    'ACTIVE',
    'IDENTITY_CHECKS_IN_PROGRESS',
    'IDENTITY_CHECKS_NEED_USER_ACTION',
    'OPENING_IN_PROGRESS',
];
// Only one GIA and one ISA can be opened, so their paused applications can't be continued once the account exists.
// A user can open a JISA for each child, so a paused JISA application is kept.
const omitOpenedAccountTypes = (pausedOnboarding, accounts) => accounts.reduce((paused, { account }) => {
    if (account.broker === 'seccl' &&
        account.accountType !== 'JISA' &&
        OPENED_ACCOUNT_STATUSES.includes(account.status)) {
        return omitPausedOnboarding(paused, account.accountType);
    }
    return paused;
}, pausedOnboarding);
const reducer = (state = initialState, action) => {
    switch (action?.type) {
        case REHYDRATE: {
            if (action.payload && action.payload.invest) {
                return {
                    ...state, // initialize new or missing keys, so that we don’t have to declare them as optionals
                    ...action.payload.invest,
                    ordersCache: {},
                    /**
                     * moving these to unpersisted invest reducer
                     */
                    // @ts-expect-error
                    account: undefined,
                    status: undefined,
                    hasTraded: undefined,
                    hasFunded: undefined,
                    activityCache: {},
                    activityIdForSymbolCache: {},
                    orderIdCache: [],
                    orderIdForSymbolCache: {},
                    pendingOrderIdCache: [],
                    pendingOrderIdForSymbolCache: {},
                    limitStopOrderIdCache: [],
                    limitStopOrderIdForSymbolCache: {},
                    currentPriceCache: {},
                    priceChangeCache: {},
                    cache: {},
                    losers: [],
                    gainers: [],
                    newlyAdded: undefined,
                    topTrending: [],
                    mostHeldStocks: [],
                    positionsCache: {},
                    positionsCaches: {},
                    mostWatchedStocks: [],
                    recentlyViewedCache: [],
                    recentlyViewedStocks: [],
                    sectorsCache: undefined,
                    recentlyViewedAssetIds: undefined,
                    collectionsCache: undefined,
                    pendingTopUpForAccountId: {},
                    firstAccountJustBecameActive: false,
                };
            }
            return state;
        }
        case LOGGED_OUT: {
            return initialState;
        }
        case localTypes.GET_REAL_TIME_PRICES_SUCCESS: {
            const data = action.payload;
            return {
                ...state,
                currentPriceCaches: {
                    ...state.currentPriceCaches,
                    [data.assetId]: {
                        value: data.price,
                        currency: data.priceCurrency,
                        mTimestamp: getMTimestamp(data.timeStamp),
                    },
                },
                priceChangeCaches: {
                    ...state.priceChangeCaches,
                    [data.assetId]: {
                        value: data.priceChange,
                        percent: data.priceChangePct,
                    },
                },
            };
        }
        case 'CLOSE_USER_TRADING_ACCOUNT_SUCCESS_ACTION': {
            const closedAccountType = state.accounts?.find(({ account }) => account.id === action.extra.accountId)?.account
                .accountType;
            return {
                ...state,
                onboarding: undefined,
                pausedOnboarding: closedAccountType
                    ? omitPausedOnboarding(state.pausedOnboarding, closedAccountType)
                    : state.pausedOnboarding,
                accounts: state.accounts?.map((account) => {
                    if (account.account.id === action.extra.accountId) {
                        return {
                            ...account,
                            account: {
                                ...account.account,
                                status: action.payload.status,
                                reopenBlockedUntil: action.payload.reopenBlockedUntil,
                            },
                        };
                    }
                    return account;
                }),
            };
        }
        case 'CLEAR_TRADING_BLOCK_EXPIRY_DATE_ACTION': {
            if (state.accounts) {
                return {
                    ...state,
                    accounts: state.accounts?.map((account) => {
                        if (account.account.broker === 'seccl') {
                            return account;
                        }
                        return {
                            ...account,
                            account: {
                                ...account.account,
                                tradingBlockExpiryDate: undefined,
                            },
                        };
                    }),
                };
            }
            return state;
        }
        case 'INVEST_ADD_BUY_BLOCK_SUCCESS_ACTION': {
            if (state.accounts) {
                return {
                    ...state,
                    accounts: state.accounts?.map((account) => {
                        if (account.account.broker === 'seccl') {
                            return account;
                        }
                        return {
                            ...account,
                            account: {
                                ...account.account,
                                tradingBlockExpiryDate: action.extra.blockUntilDate,
                            },
                        };
                    }),
                };
            }
            return state;
        }
        case 'ONBOARDING_FUNNEL_COMPLETED': {
            return {
                ...state,
                hasCompletedOnboardingFunnel: true,
            };
        }
        case 'GET_SECTORS_SUCCESS': {
            const { payload } = action;
            if (payload?.categories?.length) {
                const categoryIdSectorMap = {};
                payload.categories.forEach((category) => {
                    if (handledSectorNames.includes(category.name)) {
                        categoryIdSectorMap[category.id] = category;
                    }
                });
                return {
                    ...state,
                    categoryIdSectorMap,
                };
            }
            return state;
        }
        case 'GET_FX_FEES_SUCCESS': {
            const tierFxRateMap = {};
            action.payload.feeTiers.forEach((rateObj) => {
                tierFxRateMap[rateObj.tier] = rateObj.percentageFee.toFixed(2);
            });
            return {
                ...state,
                tierFxRateMap,
            };
        }
        case 'GET_SECCL_AUM_FEES_SUCCESS': {
            const tierSecclAUMFeesMap = {};
            Object.keys(action.payload).forEach((key) => {
                tierSecclAUMFeesMap[key] = action.payload[key].percentage.toFixed(2);
            });
            return {
                ...state,
                tierSecclAUMFeesMap,
            };
        }
        case 'GET_FUND_ALLOCATIONS_SUCCESS': {
            const { payload } = action;
            const normalizedPayload = Object.fromEntries(Object.entries(payload).map(([key, section]) => [
                key,
                section
                    ? { ...section, items: section.items.map((item) => ({ ...item, name: toTitleCase(item.name) })) }
                    : section,
            ]));
            return {
                ...state,
                fundAllocations: {
                    ...state.fundAllocations,
                    [action.extra]: normalizedPayload,
                },
            };
        }
        case 'GET_STATIC_COLLECTION_STOCKS_SUCCESS': {
            const { type } = action.extra;
            const { assets } = action.payload;
            let key = '';
            switch (type) {
                case 'BIGGEST_GAINERS':
                    key = 'gainers';
                    break;
                case 'BIGGEST_LOSERS':
                    key = 'losers';
                    break;
                case 'MOST_POPULAR':
                    key = 'mostHeldStocks';
                    break;
                default:
                    break;
            }
            if (key) {
                return {
                    ...state,
                    caches: updateStockMetaCache(state.caches, assets),
                    priceChangeCaches: updatePriceChangeCache(state.priceChangeCaches, assets),
                    currentPriceCaches: updateCurrentPriceCache(state.currentPriceCaches, assets),
                    [key]: assets.map((asset) => ({ symbol: asset.symbol, assetId: asset.assetId })),
                };
            }
            return {
                ...state,
                caches: updateStockMetaCache(state.caches, assets),
                currentPriceCaches: updateCurrentPriceCache(state.currentPriceCaches, assets),
                priceChangeCaches: updatePriceChangeCache(state.priceChangeCaches, assets),
            };
        }
        case 'SET_KYC_DOCUMENT_TYPE': {
            return {
                ...state,
                kycDocumentType: action.documentType,
            };
        }
        case localTypes.SET_ACCOUNT_STATUS: {
            const { status, broker, accountType } = action;
            if (state.accounts) {
                return {
                    ...state,
                    accounts: state.accounts?.map((account) => {
                        if (account.account.broker === broker && account.account.accountType === accountType) {
                            return {
                                ...account,
                                account: {
                                    ...account.account,
                                    status,
                                },
                            };
                        }
                        return account;
                    }),
                };
            }
            return state;
        }
        case localTypes.CREATE_TRADING_ACCOUNT_SUCCESS: {
            const { payload } = action;
            if (payload.success && state.accounts) {
                if (action.extra.jisaChildDetails) {
                    return {
                        ...state,
                        accounts: [
                            ...state.accounts,
                            {
                                hasTraded: false,
                                hasFunded: false,
                                hasPostOpenInstructions: action.extra.hasPostOpenInstructions,
                                account: {
                                    id: `JISA_placeholder_${new Date().getTime().toString()}`,
                                    broker: 'seccl',
                                    accountType: 'JISA',
                                    jisaConversionDate: '',
                                    child: action.extra.jisaChildDetails,
                                    jisaRemainingAllowanceAfterPending: {
                                        value: 0,
                                        currency: 'GBP',
                                    },
                                    status: 'IDENTITY_CHECKS_IN_PROGRESS',
                                },
                            },
                        ],
                    };
                }
                return {
                    ...state,
                    accounts: state.accounts?.map((account) => {
                        if (account.account.broker === action.extra.broker &&
                            account.account.accountType === action.extra.accountType) {
                            return {
                                ...account,
                                hasPostOpenInstructions: action.extra.hasPostOpenInstructions,
                                account: {
                                    ...account.account,
                                    ...(account.account.accountType === 'ISA' &&
                                        action.extra.accountsHeldElsewhere?.length && {
                                        accountsHeldElsewhere: action.extra.accountsHeldElsewhere,
                                    }),
                                    status: 'IDENTITY_CHECKS_IN_PROGRESS',
                                },
                            };
                        }
                        return account;
                    }),
                };
            }
            return state;
        }
        case 'CLEAR_ONBOARDING_STATE': {
            const accountType = state.onboarding?.accountType;
            return {
                ...state,
                onboarding: undefined,
                pausedOnboarding: accountType
                    ? omitPausedOnboarding(state.pausedOnboarding, accountType)
                    : state.pausedOnboarding,
            };
        }
        case 'PAUSE_ONBOARDING_STATE': {
            const accountType = state.onboarding?.accountType;
            if (!state.onboarding || !accountType) {
                return { ...state, onboarding: undefined };
            }
            return {
                ...state,
                onboarding: undefined,
                pausedOnboarding: { ...state.pausedOnboarding, [accountType]: state.onboarding },
            };
        }
        case 'RESUME_ONBOARDING_STATE': {
            const paused = state.pausedOnboarding?.[action.accountType];
            if (!paused) {
                return state;
            }
            return {
                ...state,
                onboarding: paused,
                pausedOnboarding: omitPausedOnboarding(state.pausedOnboarding, action.accountType),
            };
        }
        case 'DISCARD_PAUSED_ONBOARDING_STATE': {
            return { ...state, pausedOnboarding: omitPausedOnboarding(state.pausedOnboarding, action.accountType) };
        }
        case 'GET_TRADING_ACCOUNTS_SUCCESS': {
            const { payload } = action;
            const statusObj = state.accounts
                ? getTradingAccountStatusObj(state.accounts)
                : {
                    alpacaGIA: 'NOT_STARTED',
                    secclGIA: 'NOT_STARTED',
                    secclISA: 'NOT_STARTED',
                };
            const jisaMap = {};
            const updatedApiAccounts = payload.accounts.map((account) => {
                if (account.account.accountType === 'JISA') {
                    const { firstName, surname, dateOfBirth } = account.account.child;
                    jisaMap[`${firstName}-${surname}-${dateOfBirth}`] = account;
                }
                const key = `${account.account.broker}${account.account.accountType}`;
                if (account.account.status === 'NOT_STARTED' && statusObj[key] === 'IDENTITY_CHECKS_IN_PROGRESS') {
                    const matchedAccountFromState = state.accounts?.find((stateAccount) => account.account.broker === stateAccount.account.broker &&
                        account.account.accountType === stateAccount.account.accountType);
                    return (matchedAccountFromState || {
                        ...account,
                        account: {
                            ...account.account,
                            status: statusObj[key],
                        },
                    });
                }
                return account;
            });
            // JISA creation is async — the new account may not appear in the API response yet.
            // Keep any placeholder JISA accounts whose child (matched by name + DOB) isn't in the API response.
            const unmatchedJisaPlaceholders = (state.accounts ?? []).filter((stateAccount) => {
                if (stateAccount.account.accountType !== 'JISA')
                    return false;
                if (!stateAccount.account.id?.startsWith('JISA_placeholder_'))
                    return false;
                const { child: placeholderChild } = stateAccount.account;
                const key = `${placeholderChild.firstName}-${placeholderChild.surname}-${placeholderChild.dateOfBirth}`;
                return !(key in jisaMap);
            });
            const hadTradingAccounts = !!state.accounts?.length;
            const hadActiveAccountBefore = !!state.accounts?.some((account) => account.account.status === 'ACTIVE');
            const hasActiveAccountNow = !!payload.accounts?.some((account) => account.account.status === 'ACTIVE');
            const firstAccountBecameActive = hadTradingAccounts && !hadActiveAccountBefore && hasActiveAccountNow;
            return {
                ...state,
                accounts: [...updatedApiAccounts, ...unmatchedJisaPlaceholders],
                portfolioValue: payload.portfolioValue,
                totalInvestments: payload.totalInvestments,
                firstAccountJustBecameActive: firstAccountBecameActive || state.firstAccountJustBecameActive,
                pausedOnboarding: omitOpenedAccountTypes(state.pausedOnboarding, payload.accounts),
            };
        }
        case localTypes.CLEAR_FIRST_ACCOUNT_ACTIVATED_FLAG: {
            return { ...state, firstAccountJustBecameActive: false };
        }
        case localTypes.GET_CONNECTED_ACCOUNT_SUCCESS: {
            const { payload } = action;
            return {
                ...state,
                connectedAccount: payload.account,
            };
        }
        case 'GET_STOCKS_META_SUCCESS':
        case localTypes.GET_STOCK_META_SUCCESS: {
            const { payload } = action;
            const newCache = { ...state.caches };
            payload.companies.forEach((stockMeta) => {
                newCache[stockMeta.assetId] = {
                    ...state.caches[stockMeta.assetId],
                    id: stockMeta.assetId,
                    symbol: stockMeta.symbol,
                    iconUrl: stockMeta.iconUrl,
                    name: stockMeta.companyName,
                    originalName: stockMeta.originalName,
                    shortDescription: stockMeta.shortDescription,
                    ...(stockMeta.description
                        ? {
                            description: stockMeta.description,
                        }
                        : {}),
                    kidUrl: stockMeta.kidUrl,
                    fundRiskLevel: stockMeta.fundRiskLevel,
                    managementFee: stockMeta.managementFee,
                    tradingCharge: stockMeta.tradingCharge,
                    ongoingCharge: stockMeta.ongoingCharge,
                    transactionCost: stockMeta.transactionCost,
                    incidentalCost: stockMeta.incidentalCost,
                    platformFee: stockMeta.platformFee,
                    esgUrl: stockMeta.esgUrl,
                    type: stockMeta.type,
                    minimumTransferUnit: stockMeta.minimumTransferUnit,
                };
            });
            if (payload.companies.length > 0) {
                return { ...state, caches: newCache };
            }
            return state;
        }
        case localTypes.GET_POSITIONS_SUCCESS: {
            const { extra, payload } = action;
            const { accountId } = extra;
            const requestedAssetIds = extra.assetIds;
            const newCache = requestedAssetIds === undefined ? {} : { ...state.tradingAccountPositionsCache?.[accountId] };
            const newCurrentPriceCache = { ...state.currentPriceCaches };
            if (payload.positions.length > 0) {
                const mTimestamp = getMTimestamp();
                payload.positions.forEach((position) => {
                    newCache[position.assetId] = position; // there can only be one single position per assetId
                    // We update the current price of this assetId because we can - position contains the data about it
                    // TODO: we need to also update the price change, but it doesn’t come in this object. Maybe fire another request to API to update it, and remove 4 lines below
                    newCurrentPriceCache[position.assetId] = {
                        ...position.price,
                        mTimestamp, // TODO: use price’s timestamp from position when it will be available
                    };
                });
                return {
                    ...state,
                    tradingAccountPositionsCache: {
                        ...state.tradingAccountPositionsCache,
                        [accountId]: newCache,
                    },
                    currentPriceCaches: newCurrentPriceCache,
                    caches: updateStockMetaCache(state.caches, payload.positions),
                };
            }
            if (requestedAssetIds !== undefined) {
                // We were fetching a list of positions for specific assetIds and zero positions were returned.
                // We need to clear positions for those assetIds.
                requestedAssetIds.forEach((assetId) => {
                    newCache[assetId] = undefined; // we don’t use delete since it slows down the performance of object
                });
                return {
                    ...state,
                    tradingAccountPositionsCache: {
                        ...state.tradingAccountPositionsCache,
                        [accountId]: newCache,
                    },
                };
            }
            return {
                ...state,
                tradingAccountPositionsCache: {
                    ...state.tradingAccountPositionsCache,
                    [accountId]: newCache,
                },
            };
        }
        case localTypes.GET_ORDERS_SUCCESS: {
            const { extra, payload } = action;
            const { accountId } = extra;
            if (extra.assetIds && extra.assetIds.length > 1) {
                // Logic below doesn’t work when we were fetching for more than one assetId.
                // It only works if we were fetching one or all assetIds.
                log('[invest reducer] Please update GET_ORDERS_SUCCESS handling', false, 'yellow');
            }
            const page1Match = !extra.paging;
            const newOrdersCache = {};
            const newQueuedOrdersArr = [];
            const newPendingOrdersArr = [];
            const newLimitAndStopOrdersArr = [];
            const newQueuedOrderIdForAssetIdCache = page1Match
                ? {}
                : { ...state.orderIdForAssetIdCache };
            const newPendingOrderIdForAssetIdCache = page1Match
                ? {}
                : {
                    ...state.pendingOrderIdForAssetIdCache,
                };
            const newLimitStopOrderIdForAssetIdCache = page1Match
                ? {}
                : {
                    ...state.limitStopOrderIdForAssetIdCache,
                };
            payload.orders.forEach((order) => {
                const { id, status, assetId, orderType } = order;
                newOrdersCache[id] = order;
                if (orderType === invest.SellOrderType.MARKET) {
                    if (status === 'pending') {
                        newPendingOrdersArr.push(id);
                        const cacheForAssetId = newPendingOrderIdForAssetIdCache[assetId];
                        if (cacheForAssetId) {
                            cacheForAssetId.push(id);
                        }
                        else {
                            newPendingOrderIdForAssetIdCache[assetId] = [id];
                        }
                    }
                    else if (status === 'queued') {
                        newQueuedOrdersArr.push(id);
                        const cacheForAssetId = newQueuedOrderIdForAssetIdCache[assetId];
                        if (cacheForAssetId) {
                            cacheForAssetId.push(id);
                        }
                        else {
                            newQueuedOrderIdForAssetIdCache[assetId] = [id];
                        }
                    }
                }
                else if (orderType === invest.SellOrderType.LIMIT || orderType === invest.SellOrderType.STOP) {
                    newLimitAndStopOrdersArr.push(id);
                    const cacheForAssetId = newLimitStopOrderIdForAssetIdCache[assetId];
                    if (cacheForAssetId) {
                        cacheForAssetId.push(id);
                    }
                    else {
                        newLimitStopOrderIdForAssetIdCache[assetId] = [id];
                    }
                }
            });
            const newState = {
                ...state,
                ordersCache: {
                    ...state.ordersCache,
                    ...newOrdersCache,
                },
            };
            // case for full list pull
            if (!extra.assetIds?.length) {
                if (extra.orderTypes?.includes(invest.SellOrderType.LIMIT) ||
                    extra.orderTypes?.includes(invest.SellOrderType.STOP)) {
                    newState.limitStopOrderIdCache = page1Match
                        ? newLimitAndStopOrdersArr
                        : [...(state.limitStopOrderIdCache || []), ...newLimitAndStopOrdersArr];
                    newState.limitStopOrderIdForAssetIdCache = newLimitStopOrderIdForAssetIdCache;
                }
                else {
                    const pendingOrdersForTradingAccount = newState.pendingOrdersForTradingAccountCache[accountId] || [];
                    newState.pendingOrdersForTradingAccountCache = {
                        ...state.pendingOrdersForTradingAccountCache,
                        [accountId]: page1Match ? newPendingOrdersArr : [...pendingOrdersForTradingAccount, ...newPendingOrdersArr],
                    };
                    newState.pendingOrderIdForAssetIdCache = newPendingOrderIdForAssetIdCache;
                    const queuedOrdersForTradingAccount = newState.queuedOrdersForTradingAccountCache[accountId] || [];
                    newState.queuedOrdersForTradingAccountCache = {
                        ...state.queuedOrdersForTradingAccountCache,
                        [accountId]: page1Match ? newQueuedOrdersArr : [...queuedOrdersForTradingAccount, ...newQueuedOrdersArr],
                    };
                    newState.orderIdForAssetIdCache = newQueuedOrderIdForAssetIdCache;
                }
                return newState;
            }
            const assetId = extra.assetIds[0];
            if (extra.orderTypes?.includes(invest.SellOrderType.LIMIT) ||
                extra.orderTypes?.includes(invest.SellOrderType.STOP)) {
                const computedCache = newLimitStopOrderIdForAssetIdCache[assetId] || [];
                newState.limitStopOrderIdForAssetIdCache = {
                    ...state.limitStopOrderIdForAssetIdCache,
                    [assetId]: computedCache,
                };
            }
            else {
                const computedPendingCache = newPendingOrderIdForAssetIdCache[assetId] || [];
                newState.pendingOrderIdForAssetIdCache = {
                    ...state.pendingOrderIdForAssetIdCache,
                    [assetId]: computedPendingCache,
                };
                const computedCache = newQueuedOrderIdForAssetIdCache[assetId] || [];
                newState.orderIdForAssetIdCache = {
                    ...state.orderIdForAssetIdCache,
                    [assetId]: computedCache,
                };
            }
            return newState;
        }
        case localTypes.CREATE_ORDER_SUCCESS: {
            const { payload } = action;
            const { accountId } = action.extra;
            const { id, status, orderType, assetId } = payload.order;
            if (orderType === invest.SellOrderType.LIMIT || orderType === invest.SellOrderType.STOP) {
                const newOrdersCache = {
                    ...state.ordersCache,
                };
                newOrdersCache[id] = payload.order;
                const newLimitStopOrderIdCache = new Set([id, ...(state.limitStopOrderIdCache || [])]);
                const oldLimitStopOrderIdForAssetIdCache = state.limitStopOrderIdForAssetIdCache?.[assetId] ?? [];
                const newLimitStopOrderIdForAssetId = new Set([id, ...oldLimitStopOrderIdForAssetIdCache]);
                const newLimitStopOrderIdForAssetIdCache = {
                    ...state.orderIdForAssetIdCache,
                    [assetId]: [...newLimitStopOrderIdForAssetId],
                };
                return {
                    ...state,
                    ordersCache: newOrdersCache,
                    limitStopOrderIdForAssetIdCache: newLimitStopOrderIdForAssetIdCache,
                    limitStopOrderIdCache: [...newLimitStopOrderIdCache],
                };
            }
            if (status === 'queued') {
                const newOrdersCache = {
                    ...state.ordersCache,
                };
                const oldCacheForAssetId = state.orderIdForAssetIdCache[assetId] ?? [];
                const newCacheForAssetId = new Set([id, ...oldCacheForAssetId]);
                newOrdersCache[id] = payload.order;
                const newOrderIdForAssetIdCache = {
                    ...state.orderIdForAssetIdCache,
                    [assetId]: [...newCacheForAssetId],
                };
                const newOrderIdCache = new Set([id, ...(state.queuedOrdersForTradingAccountCache[accountId] || [])]);
                return {
                    ...state,
                    ordersCache: newOrdersCache,
                    orderIdForAssetIdCache: newOrderIdForAssetIdCache,
                    queuedOrdersForTradingAccountCache: {
                        ...state.queuedOrdersForTradingAccountCache,
                        [accountId]: [...newOrderIdCache],
                    },
                };
            }
            if (status === 'pending') {
                const newOrdersCache = {
                    ...state.ordersCache,
                };
                const oldCacheForAssetId = state.pendingOrderIdForAssetIdCache[assetId] ?? [];
                const newCacheForAssetId = new Set([id, ...oldCacheForAssetId]);
                newOrdersCache[id] = payload.order;
                const newOrderIdForAssetIdCache = {
                    ...state.pendingOrderIdForAssetIdCache,
                    [assetId]: [...newCacheForAssetId],
                };
                const newOrderIdCache = new Set([id, ...(state.pendingOrdersForTradingAccountCache[accountId] || [])]);
                return {
                    ...state,
                    ordersCache: newOrdersCache,
                    pendingOrderIdForAssetIdCache: newOrderIdForAssetIdCache,
                    pendingOrdersForTradingAccountCache: {
                        ...state.pendingOrdersForTradingAccountCache,
                        [accountId]: [...newOrderIdCache],
                    },
                };
            }
            return state;
        }
        case localTypes.GET_ORDER_SUCCESS: {
            const { payload: { order }, } = action;
            const { accountId } = action.extra;
            const { id, status, assetId } = order;
            const cachedOrder = state.ordersCache[id];
            const newState = {
                ...state,
                ordersCache: { ...state.ordersCache, [order.id]: order },
            };
            if (cachedOrder) {
                // we had this order in cache, check its status and if status changed then update our queued/pending id arrays
                const { status: cachedStatus } = cachedOrder;
                if (status !== cachedStatus) {
                    // status changed, update id lists according to that
                    if (cachedStatus === 'pending') {
                        // drop this order from pending list
                        newState.pendingOrdersForTradingAccountCache = {
                            ...state.pendingOrdersForTradingAccountCache,
                            [accountId]: state.pendingOrdersForTradingAccountCache[accountId]?.filter((pendingOrderId) => pendingOrderId !== id),
                        };
                    }
                    else if (cachedStatus === 'queued') {
                        // drop this order from queued list
                        newState.queuedOrdersForTradingAccountCache = {
                            ...state.queuedOrdersForTradingAccountCache,
                            [accountId]: state.queuedOrdersForTradingAccountCache[accountId]?.filter((queuedOrderId) => queuedOrderId !== id),
                        };
                    }
                }
            }
            // make sure this order exists in order id lists, if it should
            if (status === 'queued' && order.orderType === invest.SellOrderType.MARKET) {
                newState.queuedOrdersForTradingAccountCache = {
                    ...state.queuedOrdersForTradingAccountCache,
                    [accountId]: insertOrderId(id, state.queuedOrdersForTradingAccountCache[accountId] || [], newState.ordersCache),
                };
                const forAssetId = state.orderIdForAssetIdCache[assetId];
                if (forAssetId) {
                    newState.orderIdForAssetIdCache[assetId] = insertOrderId(id, forAssetId, newState.ordersCache);
                }
                else {
                    newState.orderIdForAssetIdCache[assetId] = [id];
                }
            }
            else if (status === 'pending' && order.orderType === invest.SellOrderType.MARKET) {
                newState.pendingOrdersForTradingAccountCache = {
                    ...state.pendingOrdersForTradingAccountCache,
                    [accountId]: insertOrderId(id, state.pendingOrdersForTradingAccountCache[accountId] || [], newState.ordersCache),
                };
                const forAssetId = state.pendingOrderIdForAssetIdCache[assetId];
                if (forAssetId) {
                    newState.pendingOrderIdForAssetIdCache[assetId] = insertOrderId(id, forAssetId, newState.ordersCache);
                }
                else {
                    newState.pendingOrderIdForAssetIdCache[assetId] = [id];
                }
            }
            return newState;
        }
        case localTypes.GET_SEARCH_RESULTS_SUCCESS:
        case 'GET_STOCK_GROUP_SUCCESS': {
            const extra = 'extra' in action ? action.extra : undefined;
            const { payload: { assets }, } = action;
            if (extra?.withPrices === false) {
                return { ...state, caches: updateStockMetaCache(state.caches, assets) };
            }
            if (assets.length) {
                const newCurrentPriceCache = { ...state.currentPriceCaches };
                const newCache = { ...state.caches };
                assets.forEach((asset) => {
                    if (asset.icon) {
                        // adding extra check
                        const idObj = asset.id ? { id: asset.id } : {};
                        newCache[asset.id] = {
                            ...state.caches[asset.id],
                            iconUrl: asset.icon,
                            name: asset.name,
                            ...(asset.symbol ? { symbol: asset.symbol } : {}),
                            ...idObj,
                            ...(asset.description ? { description: asset.description } : {}),
                            ...(asset.shortDescription ? { shortDescription: asset.shortDescription } : {}),
                            ...(asset.averageAnnualReturn && { averageAnnualReturn: asset.averageAnnualReturn }),
                            ...(asset.fundRiskLevel ? { fundRiskLevel: asset.fundRiskLevel } : {}),
                            ...(asset.fundTheme ? { fundTheme: asset.fundTheme } : {}),
                            ...(asset.fundGeo ? { fundGeo: asset.fundGeo } : {}),
                        };
                    }
                });
                let didChangePrices = false;
                const mTimestamp = getMTimestamp();
                assets.forEach((asset) => {
                    if (asset.sharePrice !== null && asset.sharePrice !== undefined && asset.sharePriceCurrency) {
                        didChangePrices = true;
                        newCurrentPriceCache[asset.id] = {
                            value: asset.sharePrice,
                            currency: asset.sharePriceCurrency,
                            mTimestamp, // TODO: use timestamp from asset when it will be there (waiting for API update)
                        };
                    }
                });
                if (didChangePrices) {
                    return {
                        ...state,
                        caches: newCache,
                        currentPriceCaches: newCurrentPriceCache,
                        priceChangeCaches: updatePriceChangeCache(state.priceChangeCaches, assets),
                    };
                }
                return { ...state, caches: newCache };
            }
            return state;
        }
        case localTypes.CANCEL_ORDER_SUCCESS: {
            const { extra: { orderId, accountId }, } = action;
            if (orderId) {
                // order is cancelled. Remove it from list of queued and pending orders (orderIdForAssetIdCache, orderIdCache and their pending alternatives)
                let didAssetIdQueuedCacheChange = false;
                let didQueuedCacheChange = false;
                let didAssetIdLimitStopCacheChange = false;
                let didLimitStopCacheChange = false;
                let didAssetIdPendingCacheChange = false;
                let didPendingCacheChange = false;
                let didCacheChange = false;
                const newOrderIdForAssetIdCache = {
                    ...state.orderIdForAssetIdCache,
                };
                const newPendingOrderIdForAssetIdCache = {
                    ...state.pendingOrderIdForAssetIdCache,
                };
                const newLimitStopOrderIdForAssetIdCache = {
                    ...state.limitStopOrderIdForAssetIdCache,
                };
                const newQueuedOrderIdCache = [...(state.queuedOrdersForTradingAccountCache[accountId] || [])];
                const newPendingOrderIdCache = [...(state.pendingOrdersForTradingAccountCache[accountId] || [])];
                const newLimitStopOrderIdCache = [...(state.limitStopOrderIdCache || [])];
                // walk thru all assetIds and look for this order, then remove it
                Object.keys(newOrderIdForAssetIdCache).forEach((assetId) => {
                    const orders = newOrderIdForAssetIdCache[assetId];
                    if (orders?.length) {
                        const index = orders.findIndex((nextOrderId) => nextOrderId === orderId);
                        if (index !== -1) {
                            orders.splice(index, 1);
                            newOrderIdForAssetIdCache[assetId] = [...orders]; // need to update reference in order for selector to update
                            didAssetIdQueuedCacheChange = true;
                        }
                    }
                });
                Object.keys(newPendingOrderIdForAssetIdCache).forEach((assetId) => {
                    const orders = newPendingOrderIdForAssetIdCache[assetId];
                    if (orders?.length) {
                        const index = orders.findIndex((nextOrderId) => nextOrderId === orderId);
                        if (index !== -1) {
                            orders.splice(index, 1);
                            newPendingOrderIdForAssetIdCache[assetId] = [...orders]; // need to update reference in order for selector to update
                            didAssetIdPendingCacheChange = true;
                        }
                    }
                });
                Object.keys(newLimitStopOrderIdForAssetIdCache).forEach((assetId) => {
                    const orders = newLimitStopOrderIdForAssetIdCache[assetId];
                    if (orders?.length) {
                        const index = orders.findIndex((nextOrderId) => nextOrderId === orderId);
                        if (index !== -1) {
                            orders.splice(index, 1);
                            newLimitStopOrderIdForAssetIdCache[assetId] = [...orders]; // need to update reference in order for selector to update
                            didAssetIdLimitStopCacheChange = true;
                        }
                    }
                });
                const index = newQueuedOrderIdCache.findIndex((nextOrderId) => nextOrderId === orderId);
                if (index !== -1) {
                    didQueuedCacheChange = true;
                    newQueuedOrderIdCache.splice(index, 1);
                }
                const indexForPending = newPendingOrderIdCache.findIndex((nextOrderId) => nextOrderId === orderId);
                if (indexForPending !== -1) {
                    didPendingCacheChange = true;
                    newPendingOrderIdCache.splice(indexForPending, 1);
                }
                const indexForLimitStop = newLimitStopOrderIdCache.findIndex((nextOrderId) => nextOrderId === orderId);
                if (indexForLimitStop !== -1) {
                    didLimitStopCacheChange = true;
                    newLimitStopOrderIdCache.splice(indexForPending, 1);
                }
                const newState = { ...state };
                const cachedOrder = state.ordersCache[orderId];
                if (cachedOrder) {
                    didCacheChange = true;
                    newState.ordersCache = { ...state.ordersCache, [orderId]: undefined }; // remove this order from cache
                }
                if (didAssetIdQueuedCacheChange) {
                    newState.orderIdForAssetIdCache = newOrderIdForAssetIdCache;
                }
                if (didAssetIdPendingCacheChange) {
                    newState.pendingOrderIdForAssetIdCache = newPendingOrderIdForAssetIdCache;
                }
                if (didAssetIdLimitStopCacheChange) {
                    newState.limitStopOrderIdForAssetIdCache = newLimitStopOrderIdForAssetIdCache;
                }
                if (didQueuedCacheChange) {
                    newState.queuedOrdersForTradingAccountCache = {
                        ...state.queuedOrdersForTradingAccountCache,
                        [accountId]: newQueuedOrderIdCache,
                    };
                }
                if (didPendingCacheChange) {
                    newState.pendingOrdersForTradingAccountCache = {
                        ...state.pendingOrdersForTradingAccountCache,
                        [accountId]: newPendingOrderIdCache,
                    };
                }
                if (didLimitStopCacheChange) {
                    newState.limitStopOrderIdCache = newLimitStopOrderIdCache;
                }
                if (didQueuedCacheChange ||
                    didAssetIdQueuedCacheChange ||
                    didPendingCacheChange ||
                    didAssetIdPendingCacheChange ||
                    didLimitStopCacheChange ||
                    didAssetIdLimitStopCacheChange ||
                    didCacheChange) {
                    return newState;
                }
            }
            return state;
        }
        case 'SAVE_ONBOARDING_STATE': {
            const { screen, params, didSeeAUMFees, accountType, didSignDeclaration, assetId, amount, fundingAccount, autoinvestDayOfMonth, accountsHeldElsewhere, hasJISAOrTrustFund, firstName, lastName, dob, childAddress, } = action;
            // Switching account type mid-onboarding discards everything saved for the previous type
            const isAccountTypeChange = !!accountType && !!state.onboarding?.accountType && accountType !== state.onboarding.accountType;
            const previous = isAccountTypeChange ? undefined : state.onboarding;
            const onboarding = {
                amount: amount
                    ? {
                        ...previous?.amount,
                        ...amount,
                    }
                    : previous?.amount,
                assetId: assetId || previous?.assetId,
                screen: screen || previous?.screen,
                fundingAccount: fundingAccount || previous?.fundingAccount,
                didSignDeclaration: didSignDeclaration || previous?.didSignDeclaration,
                params: { ...previous?.params, ...params },
                accountType: accountType || previous?.accountType,
                didSeeAUMFees: didSeeAUMFees || previous?.didSeeAUMFees,
                autoinvestDayOfMonth: autoinvestDayOfMonth || previous?.autoinvestDayOfMonth,
                accountsHeldElsewhere: accountsHeldElsewhere ?? previous?.accountsHeldElsewhere,
                hasJISAOrTrustFund: typeof hasJISAOrTrustFund === 'boolean' ? hasJISAOrTrustFund : previous?.hasJISAOrTrustFund,
                firstName: firstName || previous?.firstName,
                lastName: lastName || previous?.lastName,
                dob: dob || previous?.dob,
                childAddress: childAddress || previous?.childAddress,
            };
            return { ...state, onboarding };
        }
        case 'CACHE_TRUSTED_CONTACTS': {
            const { trustedContacts } = action;
            return { ...state, trustedContacts };
        }
        case 'SET_REFERRER_ID': {
            const { referrerId } = action;
            return { ...state, referrerId };
        }
        case 'GET_LATEST_PRICE_INFO_SUCCESS': {
            const { payload } = action;
            const updatedPriceInfoCache = {};
            const updatedCurrentPriceCache = {};
            const mTimestamp = getMTimestamp();
            Object.keys(payload).forEach((assetId) => {
                updatedPriceInfoCache[assetId] = {
                    value: payload[assetId].priceChange,
                    percent: payload[assetId].priceChangePct,
                };
                updatedCurrentPriceCache[assetId] = {
                    mTimestamp,
                    value: payload[assetId].price,
                    currency: payload[assetId].currency,
                };
            });
            return {
                ...state,
                currentPriceCaches: {
                    ...state.currentPriceCaches,
                    ...updatedCurrentPriceCache,
                },
                priceChangeCaches: {
                    ...state.priceChangeCaches,
                    ...updatedPriceInfoCache,
                },
            };
        }
        case 'ADD_RECENTLY_VIEWED_ASSET_ID_FOR_TRADING_ACCOUNT': {
            const { assetId, tradingAccountId } = action;
            const { recentlyViewedAssetIdsForTradingAccount } = state;
            const recentlyViewedAssetIds = recentlyViewedAssetIdsForTradingAccount?.[tradingAccountId];
            if (recentlyViewedAssetIds?.[0] === assetId) {
                return state; // no need to change anything, user revisited the stock that he immediately visited before
            }
            const updatedArr = recentlyViewedAssetIds ? [assetId, ...recentlyViewedAssetIds] : [assetId];
            const uniqueArr = [...new Set(updatedArr)];
            if (uniqueArr.length <= RECENTLY_VIEWED_MAX_COUNT) {
                return {
                    ...state,
                    recentlyViewedAssetIdsForTradingAccount: {
                        ...state.recentlyViewedAssetIdsForTradingAccount,
                        [tradingAccountId]: uniqueArr,
                    },
                };
            }
            const newRecentlyViewedCache = uniqueArr.slice(0, RECENTLY_VIEWED_MAX_COUNT);
            return {
                ...state,
                recentlyViewedAssetIdsForTradingAccount: {
                    ...state.recentlyViewedAssetIdsForTradingAccount,
                    [tradingAccountId]: newRecentlyViewedCache,
                },
            };
        }
        case 'CREATE_WATCHLIST_SUCCESS': {
            const { payload: { watchlist }, } = action;
            const newWatchlistCache = {
                id: watchlist.id,
                name: watchlist.name,
                emoji: watchlist.emoji,
                assets: [],
            };
            const { watchlists: watchlistsCache } = state;
            if (watchlistsCache) {
                return { ...state, watchlists: [...watchlistsCache, newWatchlistCache] }; // TODO: ask backend about sorting - will the new list be appended to the end or is there any kind of sorting?
            }
            return { ...state, watchlists: [newWatchlistCache] };
        }
        case 'GET_WATCHLISTS_SUCCESS': {
            // TODO:add assets from api response which is in progress right now
            const { watchlists } = action.payload;
            const newWatchlistCache = watchlists.map((watchlist) => ({
                ...watchlist,
                assets: [],
            }));
            return {
                ...state,
                watchlists: newWatchlistCache,
            };
        }
        case 'UPDATE_WATCHLIST_SUCCESS': {
            /**
             * Updates watchlist’s name and/or emoji
             */
            const { payload: { watchlist }, } = action;
            const { watchlists: watchlistsCache } = state;
            if (watchlistsCache) {
                const watchlistIndexInCache = watchlistsCache.findIndex((cachedItem) => cachedItem.id === watchlist.id);
                if (watchlistIndexInCache !== -1) {
                    // the watchlist that was edited was already cached. Update name and emoji from payload, and reset synced flag to true
                    return {
                        ...state,
                        watchlists: watchlistsCache.map((cachedItem, index) => {
                            if (index === watchlistIndexInCache) {
                                return {
                                    ...cachedItem,
                                    ...watchlist,
                                };
                            }
                            return cachedItem;
                        }),
                    };
                }
                // the watchlist that was edited was not even in cache yet. Add it
                const newWatchlistCache = {
                    id: watchlist.id,
                    name: watchlist.name,
                    emoji: watchlist.emoji,
                    assets: [],
                };
                return { ...state, watchlists: [...watchlistsCache, newWatchlistCache] }; // TODO: ask backend about sorting - will the new list be appended to the end or is there any kind of sorting?
            }
            const newWatchlistCache = {
                id: watchlist.id,
                name: watchlist.name,
                emoji: watchlist.emoji,
                assets: [],
            };
            return { ...state, watchlists: [newWatchlistCache] };
        }
        case 'DELETE_WATCHLIST_SUCCESS': {
            const { extra: { watchlistId }, } = action;
            const { watchlists: watchlistsCache } = state;
            if (watchlistsCache) {
                const watchlistIndexInCache = watchlistsCache.findIndex((cachedItem) => cachedItem.id === watchlistId);
                if (watchlistIndexInCache !== -1) {
                    return { ...state, watchlists: watchlistsCache.filter((cachedItem) => cachedItem.id !== watchlistId) };
                }
                // Deleted watchlist was not in cache already - do nothing.
                // In case when user tries to delete a watchlist that has not yet been synced with backend
                // (like he created the watchlist in an airplane) - there should be another action dispatched, with the offline id (of type number)
            }
            // if there was no cache, do nothing
            return state;
        }
        case 'GET_ASSET_INFO_SUCCESS': {
            const assetInfo = action.payload;
            const assetIds = Object.keys(assetInfo);
            if (!assetIds?.length) {
                return state;
            }
            const stockMetaCache = {};
            assetIds.forEach((assetId) => {
                const assetInfoForAssetId = assetInfo[assetId];
                if (!assetInfoForAssetId)
                    return;
                stockMetaCache[assetId] = {
                    ...state.caches[assetId],
                    id: assetId,
                    name: assetInfoForAssetId.name,
                    originalName: assetInfoForAssetId.originalName,
                    iconUrl: assetInfoForAssetId.icon,
                    symbol: assetInfoForAssetId.symbol,
                    ...(assetInfoForAssetId.fundTheme ? { fundTheme: assetInfoForAssetId.fundTheme } : {}),
                    ...assetInfoForAssetId.info,
                };
            });
            return {
                ...state,
                caches: {
                    ...state.caches,
                    ...stockMetaCache,
                },
            };
        }
        case 'GET_TOP_TRENDING_STOCKS_SUCCESS': {
            const { payload: { assets }, extra: { accountId }, } = action;
            return {
                ...state,
                caches: updateStockMetaCache(state.caches, assets),
                priceChangeCaches: updatePriceChangeCache(state.priceChangeCaches, assets),
                currentPriceCaches: updateCurrentPriceCache(state.currentPriceCaches, assets),
                topTrendingForTradingAccount: {
                    ...state.topTrendingForTradingAccount,
                    [accountId]: assets.map((asset) => ({ symbol: asset.symbol, assetId: asset.id })),
                },
            };
        }
        case 'GET_MOST_WATCHED_STOCKS_SUCCESS': {
            const { payload: { assets }, } = action;
            return {
                ...state,
                caches: updateStockMetaCache(state.caches, assets),
                priceChangeCaches: updatePriceChangeCache(state.priceChangeCaches, assets),
                currentPriceCaches: updateCurrentPriceCache(state.currentPriceCaches, assets),
                mostWatchedStocks: assets.map((asset) => ({ symbol: asset.symbol, assetId: asset.id })),
            };
        }
        case 'GET_MOST_HELD_STOCKS_SUCCESS': {
            const { payload: { assets }, } = action;
            return {
                ...state,
                caches: updateStockMetaCache(state.caches, assets),
                priceChangeCaches: updatePriceChangeCache(state.priceChangeCaches, assets),
                currentPriceCaches: updateCurrentPriceCache(state.currentPriceCaches, assets),
                mostHeldStocks: assets.map((asset) => ({ symbol: asset.symbol, assetId: asset.assetId })),
            };
        }
        case 'GET_WATCHLIST_SUCCESS': {
            const { assets } = action.payload.watchlist;
            const updatedMetaCache = {};
            const updatedCurrentPrice = {};
            const updatedPriceChange = {};
            const mTimestamp = getMTimestamp();
            assets.forEach((asset) => {
                const { sharePrice, sharePriceCurrency, sharePriceChange, sharePriceChangePct, name, icon, id, symbol } = asset;
                if (sharePrice !== undefined && sharePriceCurrency) {
                    updatedCurrentPrice[id] = {
                        mTimestamp,
                        value: sharePrice,
                        currency: sharePriceCurrency,
                    };
                }
                if (name && icon) {
                    updatedMetaCache[id] = {
                        ...state.caches[id],
                        id,
                        name,
                        iconUrl: icon,
                        ...(symbol ? { symbol } : {}),
                    };
                }
                if (sharePriceChange !== undefined && sharePriceChangePct !== undefined) {
                    updatedPriceChange[id] = {
                        value: sharePriceChange,
                        percent: sharePriceChangePct,
                    };
                }
            });
            return {
                ...state,
                caches: {
                    ...state.caches,
                    ...updatedMetaCache,
                },
                currentPriceCaches: {
                    ...state.currentPriceCaches,
                    ...updatedCurrentPrice,
                },
                priceChangeCaches: {
                    ...state.priceChangeCaches,
                    ...updatedPriceChange,
                },
            };
        }
        case 'GET_PENDING_TOPUPS_SUCCESS': {
            const { payload } = action;
            return {
                ...state,
                pendingTopupsFlag: payload.pendingTopUps.length > 0,
            };
        }
        case 'GET_PENDING_TOPUPS_FOR_TRADING_ACCOUNT_SUCCESS': {
            return {
                ...state,
                pendingTopUpInfoForAccountId: {
                    ...state.pendingTopUpInfoForAccountId,
                    [action.extra.accountId]: {
                        hasPendingTopups: !!action.payload.count,
                        pendingTopUps: action.payload.transactions,
                    },
                },
            };
        }
        case 'TOPUP_CREATED': {
            return { ...state, pendingTopupsFlag: true };
        }
        case 'GET_REQUIRED_NATIONAL_IDENTIFIERS_SUCCESS': {
            const requiredNationalityIdentifiersWithCountry = action.payload.requiredNationalityIdentifiers.map((identifier) => ({
                ...identifier,
                country: action.payload.nationality,
            }));
            return {
                ...state,
                requiredNationalityIdentifiers: requiredNationalityIdentifiersWithCountry,
            };
        }
        case 'POST_REQUIRED_NATIONAL_IDENTIFIERS_SUCCESS': {
            const { type, value } = action.extra;
            if (value) {
                return {
                    ...state,
                    requiredNationalityIdentifiers: [],
                };
            }
            const filteredArr = state.requiredNationalityIdentifiers?.filter((identifier) => identifier.type !== type) || [];
            return {
                ...state,
                requiredNationalityIdentifiers: filteredArr,
            };
        }
        case 'GET_SPACE_TRADING_ACCOUNTS_SUCCESS': {
            const { tradingAccounts } = action.payload;
            const spaceTradingAccounts = { ...state.spaceTradingAccounts };
            spaceTradingAccounts[action.extra.spaceId] = tradingAccounts
                .filter((acc) => acc.marketValue || acc.accountType === 'JISA')
                .map((account) => ({
                id: account.accountId,
                userId: account.userId,
                broker: account.broker,
                balance: account.marketValue,
                isEmmaInvestmentAccount: true,
                spaceId: action.extra.spaceId,
                accountType: account.accountType,
                name: account.displayName || 'Emma Invest',
            }));
            return {
                ...state,
                spaceTradingAccounts,
            };
        }
        case 'SET_TRADING_ACCOUNT_SPACE_SUCCESS': {
            const { spaceId, tradingAccountId } = action.extra;
            return {
                ...state,
                spaceTradingAccounts: undefined,
                accounts: state.accounts?.map((account) => {
                    if (account.account.id === tradingAccountId) {
                        return {
                            ...account,
                            account: {
                                ...account.account,
                                spaceId,
                            },
                        };
                    }
                    return account;
                }),
            };
        }
        case localTypes.GET_AUTOINVEST_SUCCESS: {
            const { accountId } = action.extra;
            const newPlans = accountId
                ? { ...state.autoInvestPlans, [accountId]: null }
                : (state.accounts ?? []).reduce((acc, { account }) => {
                    acc[account.id] = null;
                    return acc;
                }, {});
            for (const plan of action.payload.plans) {
                newPlans[plan.accountId] = plan;
            }
            return {
                ...state,
                autoInvestPlans: newPlans,
                autoInvestTierFees: action.payload.tierFees,
                autoInvestFundingAccount: action.payload.fundingAccount ?? undefined,
                autoInvestDirectDebitMandateActive: action.payload.directDebitMandateActive,
            };
        }
        case localTypes.CREATE_AUTOINVEST_PLAN_SUCCESS: {
            return {
                ...state,
                autoInvestPlans: {
                    ...state.autoInvestPlans,
                    [action.payload.plan.accountId]: action.payload.plan,
                },
            };
        }
        case localTypes.EDIT_AUTOINVEST_PLAN_SUCCESS:
        case localTypes.RESUME_AUTOINVEST_PLAN_SUCCESS:
        case localTypes.PAUSE_AUTOINVEST_PLAN_SUCCESS: {
            return {
                ...state,
                autoInvestPlans: {
                    ...state.autoInvestPlans,
                    [action.payload.plan.accountId]: action.payload.plan,
                },
            };
        }
        case localTypes.CANCEL_AUTOINVEST_PLAN_SUCCESS: {
            const { [action.extra.accountId]: _, ...remainingPlans } = state.autoInvestPlans ?? {};
            return {
                ...state,
                autoInvestPlans: remainingPlans,
            };
        }
        case localTypes.ACCEPT_AUTOINVEST_MANDATE_SUCCESS: {
            return {
                ...state,
                connectedAccount: state.connectedAccount
                    ? {
                        ...state.connectedAccount,
                        investMandateStatus: action.payload.mandateStatus === 'accepted' ? 'ACTIVE' : 'NONE',
                    }
                    : state.connectedAccount,
                autoInvestDirectDebitMandateActive: action.payload.mandateStatus === 'accepted',
            };
        }
        case 'ACCEPT_AUTOSAVE_MANDATE_SUCCESS': {
            return {
                ...state,
                connectedAccount: state.connectedAccount
                    ? {
                        ...state.connectedAccount,
                        potsMandateStatus: action.payload.mandateStatus === 'accepted' ? 'ACTIVE' : 'NONE',
                    }
                    : state.connectedAccount,
            };
        }
        case localTypes.EDIT_JISA_CHILD_ADDRESS_SUCCESS: {
            const { accountId, address } = action.extra;
            return {
                ...state,
                accounts: state.accounts?.map((accountObj) => {
                    if (accountObj.account.id === accountId && accountObj.account.accountType === 'JISA') {
                        return {
                            ...accountObj,
                            account: {
                                ...accountObj.account,
                                child: {
                                    ...accountObj.account.child,
                                    address,
                                },
                            },
                        };
                    }
                    return accountObj;
                }),
            };
        }
        case localTypes.CREATE_PRODUCT_TRANSFER_SUCCESS: {
            const { accountId } = action.extra;
            const { productTransferId } = action.payload;
            return {
                ...state,
                accounts: state.accounts?.map((accountObj) => {
                    if (accountObj.account.id === accountId && accountObj.account.accountType === 'JISA') {
                        return {
                            ...accountObj,
                            account: {
                                ...accountObj.account,
                                ongoingProductTransferId: productTransferId,
                            },
                        };
                    }
                    if (accountObj.account.id === accountId &&
                        accountObj.account.accountType === 'ISA' &&
                        accountObj.account.accountsHeldElsewhere?.length) {
                        return {
                            ...accountObj,
                            account: {
                                ...accountObj.account,
                                accountsHeldElsewhere: undefined,
                            },
                        };
                    }
                    return accountObj;
                }),
            };
        }
        case localTypes.TRANSFER_PRODUCT_LATER_REQUEST: {
            return {
                ...state,
                accounts: state.accounts?.map((accountObj) => {
                    if (accountObj.account.accountType === 'ISA') {
                        return {
                            ...accountObj,
                            account: {
                                ...accountObj.account,
                                accountsHeldElsewhere: undefined,
                            },
                        };
                    }
                    return accountObj;
                }),
            };
        }
        default: {
            return state;
        }
    }
};
export default reducer;
